/** A threshold as the screens use it, with its unit always set (see `withThresholdUnits`). */
export interface HoleToleranceThreshold {
  metric: string;
  /** Unit of the bounds: the one the site chose for BULK (see `bulkUnitOf`), the fixed one otherwise. */
  unit: string;
  asDesignedMax: number;
  inToleranceMax: number;
  /** Signed lower-bound threshold (typically <= 0) for the negative side. Omit/undefined for distance-only metrics with no negative side. */
  asDesignedMin?: number;
  inToleranceMin?: number;
}

export interface SiteHoleTolerance {
  siteId: number;
  thresholds: HoleToleranceThreshold[];
}

/**
 * A threshold as the API sends and receives it (`Sites/{siteId}/hole-tolerance`): a unit for BULK only,
 * null for the other metrics and for BULK thresholds saved before the unit could be chosen.
 */
export interface HoleToleranceThresholdDto extends Omit<HoleToleranceThreshold, 'unit'> {
  unit?: BulkUnit | null;
}

export interface SiteHoleToleranceDto {
  siteId: number;
  thresholds: HoleToleranceThresholdDto[];
}

export function getThreshold(tolerance: SiteHoleTolerance | null | undefined, metric: string): HoleToleranceThreshold | undefined {
  return tolerance?.thresholds.find(t => t.metric === metric);
}

/** Metrics a site must set before saving its tolerance. */
export const REQUIRED_HOLE_TOLERANCE_METRICS: readonly string[] = ['HEAD', 'TOE_POSITION', 'LENGTH', 'STEMMING', 'LINEAR_QUANTITY'];

/** Every metric of the tolerance screens, with its unit and the values offered to a site without tolerance. */
export const DEFAULT_HOLE_TOLERANCE_THRESHOLDS: readonly HoleToleranceThreshold[] = [
  { metric: 'HEAD',            unit: 'm',    asDesignedMax: 0.5, inToleranceMax: 1 },
  { metric: 'TOE_POSITION',    unit: 'm',    asDesignedMax: 1,   inToleranceMax: 2 },
  { metric: 'LENGTH',          unit: 'm',    asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.5, inToleranceMin: -1 },
  { metric: 'STEMMING',        unit: 'm',    asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.2, inToleranceMin: -0.5 },
  { metric: 'LINEAR_QUANTITY', unit: 'kg/m', asDesignedMax: 0.5, inToleranceMax: 1, asDesignedMin: -0.5, inToleranceMin: -1 },
  // % of the hole's planned bulk by default (10 kg off weighs more on 100 kg than on 1 t); the site may choose kg.
  { metric: 'BULK',            unit: '%',    asDesignedMax: 5,   inToleranceMax: 10, asDesignedMin: -5, inToleranceMin: -10 },
];

/** Units a site can choose for its bulk threshold: % of the hole's planned bulk, or kg. */
export const BULK_UNITS = ['%', 'kg'] as const;

export type BulkUnit = typeof BULK_UNITS[number];

/** Unit of a bulk threshold: the one the site chose, % when there is none (thresholds saved before the choice). */
export function bulkUnitOf(threshold: { unit?: string | null } | null | undefined): BulkUnit {
  return threshold?.unit === 'kg' ? 'kg' : '%';
}

/** In %, a hole cannot be under-loaded by more than -100 % (empty): the under-loading bounds cannot go lower. */
export const BULK_PERCENT_FLOOR = -100;

/** True for a bulk threshold in % whose under-loading bounds go below `BULK_PERCENT_FLOOR`. */
export function bulkBelowPercentFloor(threshold: HoleToleranceThreshold): boolean {
  return threshold.metric === 'BULK' && bulkUnitOf(threshold) === '%'
    && [threshold.asDesignedMin, threshold.inToleranceMin].some(bound => bound != null && bound < BULK_PERCENT_FLOOR);
}

/** Thresholds as the API returns them (a unit for BULK only), with each metric's unit. */
export function withThresholdUnits(thresholds: readonly HoleToleranceThresholdDto[]): HoleToleranceThreshold[] {
  return thresholds.map(threshold => ({
    ...threshold,
    unit: threshold.metric === 'BULK'
      ? bulkUnitOf(threshold)
      : DEFAULT_HOLE_TOLERANCE_THRESHOLDS.find(d => d.metric === threshold.metric)?.unit ?? '',
  }));
}

/** Thresholds as the API takes them: the unit chosen for BULK, none for the metrics with a fixed unit. */
export function toThresholdDtos(thresholds: readonly HoleToleranceThreshold[]): HoleToleranceThresholdDto[] {
  return thresholds.map(threshold => ({
    ...threshold,
    unit: threshold.metric === 'BULK' ? bulkUnitOf(threshold) : null,
  }));
}

/** A site tolerance as the API returns it, with each metric's unit. */
export function toSiteHoleTolerance(dto: SiteHoleToleranceDto): SiteHoleTolerance {
  return { ...dto, thresholds: withThresholdUnits(dto.thresholds ?? []) };
}

/**
 * Adds the metrics a site has no row for (BULK on sites created before it existed), unset (0) as
 * the API seeds them on a new site, so that the tolerance screens can set them.
 */
export function withMissingThresholds(thresholds: readonly HoleToleranceThreshold[]): HoleToleranceThreshold[] {
  const missing = DEFAULT_HOLE_TOLERANCE_THRESHOLDS
    .filter(d => !thresholds.some(threshold => threshold.metric === d.metric))
    .map(d => ({
      ...d,
      asDesignedMax: 0,
      inToleranceMax: 0,
      ...(d.asDesignedMin != null ? { asDesignedMin: 0, inToleranceMin: 0 } : {}),
    }));
  return [...thresholds, ...missing];
}
